import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp.tsx";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { OTPsSendResponse } from "@stytch/vanilla-js";
import { useStytch } from "@stytch/react";
import { getStytchErrorMessage } from "@/lib/utils/stytch-errors";

export const OTP_RESEND_COOLDOWN_SECONDS = 15;
const OTP_SEND_OPTIONS = { expiration_minutes: 5 };

type Channel = "sms" | "whatsapp";
type Pending = Channel | "verify" | null;

interface VerificationModalProps {
  open: boolean;
  onClose: () => void;
  otpResponse: OTPsSendResponse | null;
  setOtpResponse: (response: OTPsSendResponse) => void;
  phoneNumber: string;
  handleSuccess: (otpResponse: OTPsSendResponse) => void;
  onResend?: () => Promise<OTPsSendResponse>;
}

export function VerificationModal({
  open,
  onClose,
  otpResponse,
  setOtpResponse,
  phoneNumber,
  handleSuccess,
  onResend,
}: VerificationModalProps) {
  const stytchClient = useStytch();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [otp, setOtp] = useState("");
  const [channel, setChannel] = useState<Channel>("sms");
  const [secondsLeft, setSecondsLeft] = useState(OTP_RESEND_COOLDOWN_SECONDS);
  const requestId = useRef(0);
  const inFlight = useRef(false);

  const resendReady = secondsLeft === 0;
  const busy = pending !== null;

  useEffect(() => {
    requestId.current += 1;
    inFlight.current = false;
    setPending(null);
    setOtp("");
    setError(null);
    setChannel("sms");
    setSecondsLeft(OTP_RESEND_COOLDOWN_SECONDS);
    if (!open) return;

    const intervalId = window.setInterval(() => {
      setSecondsLeft((remaining) => Math.max(0, remaining - 1));
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [open]);

  const sendCode = async (method: Channel) => {
    if (!resendReady || inFlight.current) return;

    inFlight.current = true;
    const id = requestId.current;
    setError(null);
    setPending(method);

    try {
      const response =
        method === "whatsapp"
          ? await stytchClient.otps.whatsapp.send(phoneNumber, OTP_SEND_OPTIONS)
          : onResend
            ? await onResend()
            : await stytchClient.otps.sms.send(phoneNumber, OTP_SEND_OPTIONS);

      if (id !== requestId.current) return;
      setOtpResponse(response);
      setChannel(method);
      setOtp("");
      setSecondsLeft(OTP_RESEND_COOLDOWN_SECONDS);
    } catch (e) {
      if (id === requestId.current) setError(getStytchErrorMessage(e));
    } finally {
      if (id === requestId.current) {
        inFlight.current = false;
        setPending(null);
      }
    }
  };

  const verifyOtp = async () => {
    const methodId = otpResponse?.method_id;
    if (!methodId || !otp || inFlight.current) return;

    inFlight.current = true;
    const id = requestId.current;
    setError(null);
    setPending("verify");

    try {
      const response = await stytchClient.otps.authenticate(otp, methodId, {
        session_duration_minutes: 60 * 24,
      });
      if (id === requestId.current && response.session?.session_id) {
        handleSuccess(response);
      }
    } catch (e) {
      if (id === requestId.current) setError(getStytchErrorMessage(e));
    } finally {
      if (id === requestId.current) {
        inFlight.current = false;
        setPending(null);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent
        className="w-full max-w-[450px] p-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="border-b-[1px] border-[#E5E7EB] p-4">
          <DialogTitle className="text-2xl">Verification</DialogTitle>
          <DialogDescription className="text-sm">
            {channel === "whatsapp"
              ? "Enter the code sent to WhatsApp. The previous code will no longer work."
              : "Enter the code sent to your mobile phone"}
          </DialogDescription>
        </DialogHeader>
        <div className="mx-auto flex w-full max-w-[410px] flex-col items-center gap-4 p-4">
          <img src="assets/verification-icon.png" alt="Icon" className="mb-6" />

          {error && (
            <div className="rounded-md bg-red-200 p-4 text-sm font-medium text-red-500">
              {error}
            </div>
          )}

          <InputOTP maxLength={6} value={otp} onChange={setOtp}>
            {Array.from({ length: 6 }, (_, index) => (
              <InputOTPGroup key={index}>
                <InputOTPSlot index={index} className="h-14 w-14 text-lg" />
              </InputOTPGroup>
            ))}
          </InputOTP>

          <Button
            className="h-12 w-full rounded-lg text-lg font-bold"
            onClick={verifyOtp}
            disabled={busy}
            aria-busy={pending === "verify"}
          >
            {pending === "verify" && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Continue
          </Button>

          <div className="mt-1 flex w-full flex-col gap-2 border-t border-[#E5E7EB] pt-4">
            <p className="text-center text-sm text-[#9a8f80]">
              Didn&apos;t receive a code?
            </p>
            <ResendButton
              pending={pending === "sms"}
              disabled={!resendReady || busy}
              label={
                resendReady ? "Resend SMS" : `Resend SMS in ${secondsLeft}s`
              }
              onClick={() => sendCode("sms")}
            />
            <ResendButton
              pending={pending === "whatsapp"}
              disabled={!resendReady || busy}
              label={
                resendReady
                  ? "Send via WhatsApp"
                  : `Send via WhatsApp in ${secondsLeft}s`
              }
              onClick={() => sendCode("whatsapp")}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResendButton({
  pending,
  disabled,
  label,
  onClick,
}: {
  pending: boolean;
  disabled: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      className="h-11 w-full rounded-lg text-sm font-semibold tabular-nums"
      onClick={onClick}
      disabled={disabled}
      aria-busy={pending}
    >
      {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
      {label}
    </Button>
  );
}
